import type React from "react"
import { useState } from "react"
import styled, { css } from "styled-components"
import { ZIndexRange } from "~/types/style"
// IMPORTED FROM OLD REPO
// TODO: This component needs work
import {
  SelectMultiple as BaseSelectMultiple,
  type SelectMultipleProps as BaseSelectMultipleProps,
  Loader,
  type SelectMultipleOption,
} from "~/ur-react-components"
import { overloadColor } from "~/utils/style"
import type { SelectWrapperProps } from "./OldSelect"

interface WrapperProps extends SelectWrapperProps {
  filterBackground?: string
  optionHoverBackground?: string
  withSelectAll: boolean
  grayed?: boolean
  open: boolean
  withIcons?: boolean
}

const Wrapper = styled.div<WrapperProps>`
  position: relative;

  .--select-display {
    height: 48px !important;
    min-height: unset !important;

    box-sizing: border-box;
    padding: 1px 0.5rem 0px;

    border-radius: 4px;
    border: 1px solid ${overloadColor("gray70")};
  }

  ${(props) =>
    props.withIcons &&
    `
      .--select-display-selected > span {
        display: flex;
        align-items: center;

        svg {
          width: ${props.iconWidth || "1rem"};
          margin-right: 10px;
        }
      }
    `}

  .--select-display-inner {
    color: ${(props) => props.theme.colors.gray60};
    svg {
      fill: ${(props) => props.theme.colors.gray60};
    }
  }

  .--select-display-selected {
    padding: 4px 6px;
    font-size: 0.8rem;
    font-weight: 500;
    border-radius: 6px;
  }

  .--select-placeholder {
    color: ${(props) => props.color ?? props.theme.colors.gray50};
  }
  .--select-curtain {
    border-color: ${(props) => props.theme.colors.gray90};
    background: ${(props) => props.grayed && props.theme.colors.primary10};
  }
  .--select-curtain-option {
    padding: 1rem;
    font-size: 1rem;

    :hover {
      background: ${(props) => props.theme.colors.primary99};
      color: ${(props) => props.theme.colors.primary10};
    }

    ${(props) =>
      props.withIcons &&
      `
        & > span {
          height: 100%;
          display: flex;
          align-items: center;
        }
        svg {
          width: ${props.iconWidth || "1rem"};
          margin-right: 10px;
        }
      `}
  }
  .--select-curtain-search {
    &::before {
      margin-left: 0.6rem;
      margin-top: -2px;
    }
    input {
      background: ${(props) =>
        props.filterBackground
          ? overloadColor(props.filterBackground)
          : props.grayed
            ? "white"
            : props.theme.colors.primary99};
      padding: 0.6rem 0.6rem 0.6rem 2.2rem;
      border-radius: 8px;
      font-size: 1rem;
    }
  }

  ${(props) =>
    props.open &&
    `
    .--select-display {
      padding: 0px calc(0.5rem - 1px) 1px;
      border: 2px solid;
      border-color: ${props.theme.colors.primary40};
      border-bottom-right-radius: 0px;
      border-bottom-left-radius: 0px;
      border-bottom: none;
    }
    .--select-display-inner {
      color: ${props.theme.colors.gray0};
      svg {
        fill: ${props.theme.colors.gray0};
      }
    }

    .--select-curtain {
      border: 2px solid;
      border-top: none;
      border-color: ${props.theme.colors.primary40};
    }
  `}
`

interface SelectMulitpleRelativeWrapperProps {
  width: string
}

const SelectMultipleRelativeWrapper = styled.div<SelectMulitpleRelativeWrapperProps>`
  position: relative;
  width: ${(props) => props.width};
`

interface SelectMulitpleWrapperProps {
  open: boolean
  numSelectedText: string
  compact: boolean
  width: string
}

const SelectMulitpleWrapper = styled.div<SelectMulitpleWrapperProps>`
  width: ${(props) => props.width};
  ${(props) =>
    !props.open
      ? css`
          ${
            props.compact &&
            `
          .--select-display-inner > :not(:first-child), .--select-display-selected > i, .--select-display-selected span {
            display: none;
          }
          .--select-display-selected :after {
            content: '${props.numSelectedText}';
          }
        `
          }
        `
      : `
      z-index: ${ZIndexRange.Extra};
    `}
`

interface LoadingProps {
  background?: string
}
const Loading = styled.div<LoadingProps>`
  position: absolute;
  z-index: 1;
  top: 3px;
  left: 1rem;

  display: flex;
  align-items: center;
  height: calc(100% - 6px);
  background: ${(props) => overloadColor(props.background ?? "white")};
`
export type SelectMultipleOnSelectAll = (
  options: SelectMultipleOption<string | number, any>[],
  setSelectAll: React.Dispatch<React.SetStateAction<boolean>>,
) => (value: boolean) => void

export interface SelectMultipleProps<
  ValueType extends string | number,
  ExtraType = any,
> extends BaseSelectMultipleProps<ValueType, ExtraType> {
  loading?: boolean
  filterBackground?: string
  optionHoverBackground?: string
  withSelectAll?: boolean
  grayed?: boolean

  selectWrapperProps?: Omit<WrapperProps, "withSelectAll" | "open">
  onSelectAll?: SelectMultipleOnSelectAll
}

export const SelectMultiple = <
  ValueType extends string | number,
  ExtraType = any,
>({
  className,

  loading = false,
  grayed = false,
  filterBackground,
  optionHoverBackground,
  withSelectAll = false,

  width = "100%",

  onSelectAll,

  selectWrapperProps,
  ...selectProps
}: SelectMultipleProps<ValueType, ExtraType>) => {
  const [open, setOpen] = useState(false)

  function changeOpen(open: boolean) {
    setOpen(open)
  }

  return (
    <SelectMultipleRelativeWrapper width={width}>
      <SelectMulitpleWrapper
        open={open}
        width={width}
        compact={true}
        numSelectedText={`${selectProps.selected.length} selected`}
      >
        <Wrapper
          open={open}
          className={className}
          filterBackground={filterBackground}
          optionHoverBackground={optionHoverBackground}
          grayed={grayed}
          withSelectAll={withSelectAll}
          {...selectWrapperProps}
        >
          {loading && (
            <Loading background={selectProps.background}>
              <Loader.Dots size={22} />
            </Loading>
          )}
          <BaseSelectMultiple
            minHeight="51px"
            iconProps={{
              color: selectProps.color ?? "gray50",
              type: "solid",
              size: "1.2rem !important",
              translateY: "1px",
              ...selectProps.iconProps,
            }}
            {...selectProps}
            onOpen={() => changeOpen(true)}
            onClose={() => changeOpen(false)}
          />
        </Wrapper>
      </SelectMulitpleWrapper>
    </SelectMultipleRelativeWrapper>
  )
}
